import { Footer } from '@/previewThemes/hyper/Footer';
import { Header } from '@/previewThemes/hyper/Header';
import { Product } from '@/previewThemes/hyper/Product';
import { HYPER_PRESETS } from '@/previewThemes/hyper/presets';
import {
  hyperProductParts,
  hyperProductSections,
} from '@/previewThemes/hyper/productPage';
import type { ThemePackage } from '@/previewThemes/types';

export const theme: ThemePackage = {
  Header,
  Footer,
  Product,
  // See `presets.ts`: this theme's own shelf of the add-section library,
  // which is arrangement and copy rather than any new kind of section.
  presets: HYPER_PRESETS,
  listing: {
    // Four across beside the filter rail, five where the window allows it -
    // which is what the widest page column in the set buys. A superstore
    // catalogue is compared side by side, and a shopper choosing between nine
    // dining chairs wants them on one screen.
    grid: 'grid-cols-2 gap-6 lg:grid-cols-3 xl:grid-cols-4',
    // A furniture shop is filtered by room, material, colour and price at
    // once, and a row of chips cannot hold four axes of that.
    filters: 'sidebar',
    columns: 4,
    // The plain card: a cut-out on a rounded grey tile with the name and price
    // loose underneath. No border, because this theme separates with the tile
    // rather than with a rule - see the palette note in `meta.ts`.
    card: 'standard',
  },
  // Filled, not outlined: the grey tile is this theme's panel everywhere else,
  // so a summary beside a basket is the same grey rounded box as a product
  // tile, a shelf circle and the search box in the masthead.
  cart: { grid: 'mt-8 grid gap-10 lg:grid-cols-3', panel: 'filled' },
  checkout: {
    grid: 'mt-8 grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_24rem] lg:gap-14',
    panel: 'filled',
  },
  search: {
    grid: 'mt-8 grid gap-10 lg:grid-cols-[260px_1fr]',
    results: 'grid grid-cols-2 gap-6 lg:grid-cols-3 xl:grid-cols-4',
    columns: 4,
    card: 'standard',
    // The one panel left bare. A facet rail already reads as a column of
    // controls, and putting it in the same grey box as the results beside it
    // makes two panels where the page wants one.
    panel: 'bare',
  },
  blog: { list: 'grid gap-8 sm:grid-cols-2 lg:grid-cols-3', title: 'text-lg' },
  // Six across, matching the row the header's circular rail carries and the
  // grid the home page repeats it in: the department index of a shop with
  // twelve of them is a lineup to scan, not a set of feature cards to read.
  category: { grid: 'grid gap-6 sm:grid-cols-3 lg:grid-cols-6', tile: 'card' },
  /**
   * The product page, as the stack of sections a merchant first finds.
   *
   * This theme used to draw its product page rather than arrange it: a counter
   * and a magazine, both markup, and a customiser that could offer a merchant
   * on Hyper exactly one shelf to put anything on. Every band of it is a
   * section now - see `productPage.ts` - which is what makes "move the reviews
   * above the long reads", "take the social wall off" and "write your own
   * promises" things a merchant does rather than asks for.
   *
   * No `available` list: the whole palette is offered here on purpose. A
   * superstore's product page is a merchandising page, and a theme that
   * narrowed it would be taking back what this change is for.
   */
  pages: {
    product: { defaults: hyperProductSections },
  },
  // And the components inside the product itself, in the four regions this
  // design puts them in: the breadcrumb over both columns, the photograph and
  // the folded facts on the left, the transaction on the right, and the
  // comparison and the suggestions underneath.
  parts: {
    product_details: {
      defaults: hyperProductParts,
      presentation: {
        classes: {
          product_title: 'text-3xl sm:text-[2.5rem]',
          product_price: 'text-3xl font-extrabold',
          product_brand: 'text-[13px]',
          product_availability: 'text-[13px] font-semibold',
          product_short_description: 'text-[15px] leading-relaxed',
          product_highlights: 'text-[12px]',
          custom_text: 'text-[13px]',
        },
        // This shop's buttons are pill-shaped and run the width of the column
        // they stand in: two ways out of the page, one under the other.
        wideButtons: true,
        gallery: { aspect: 'square', thumbs: 'below', rounded: true },
      },
    },
  },
};
